// src/hooks/useOrderValidation.ts
// Hook para validación de OrderForm

import { useState, useCallback, useEffect } from 'react'
import type { OrderData, PagoParcial } from '../../types/orderFormTypes'
import {
  BOLD_PARTIAL_MIN_AMOUNT_COP,
  getBoldOrderAmountValidationError,
  isBoldAmountRangeErrorMessage,
  isBoldOrderAmountValid
} from '../../constants/boldPayments'
import {
  isStockAvailable as isStockAvailableUtil,
  calculateTotals,
  getOrderTotalCopAmount
} from '../../utils/orderFormUtils'
import { getOrderStockDisplayOptions } from '../../utils/productStockDisplayUtils'
import type { BodegaRecogidaSeleccion } from './useRecogeEnBodega'
import { isStoreAllowedForRole } from '../../constants/managerMarketplaceRole'
import { isClienteNetoActive } from '../../utils/clienteNetoOrder'

interface UseOrderValidationProps {
  formData: OrderData
  mode: 'view' | 'edit' | 'create'
  recogeEnBodega: boolean
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  pagosParciales: PagoParcial[]
  productDetails: { [key: string]: any }
  /** Igual que usePagosParciales: estado local de OrderForm (envío raíz o extraDetails al cargar) */
  costoEnvio: string | number
}

export function useOrderValidation({
  formData,
  mode,
  recogeEnBodega,
  bodegaRecogidaSeleccion,
  pagosParciales,
  productDetails,
  costoEnvio
}: UseOrderValidationProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const getPaymentMethod = useCallback(() => {
    return (formData.extraDetails?.payment_method || formData.payment_method || '')
      .toString()
      .trim()
      .toLowerCase()
  }, [formData.extraDetails?.payment_method, formData.payment_method])

  const getOrderTotalCop = useCallback(() => {
    return getOrderTotalCopAmount(
      formData.lineItems,
      productDetails,
      costoEnvio,
      formData.extraDetails
    )
  }, [formData.lineItems, formData.extraDetails, productDetails, costoEnvio])

  const isBoldPaymentAmountValid = useCallback(() => {
    if (mode !== 'create') return true
    if (getPaymentMethod() !== 'bold') return true
    return isBoldOrderAmountValid(getOrderTotalCop())
  }, [mode, getPaymentMethod, getOrderTotalCop])

  useEffect(() => {
    if (mode !== 'create') return

    const paymentMethod = getPaymentMethod()
    setErrors((prev) => {
      if (paymentMethod !== 'bold') {
        if (!isBoldAmountRangeErrorMessage(prev.payment_method)) return prev
        const next = { ...prev }
        delete next.payment_method
        return next
      }

      const amountError = getBoldOrderAmountValidationError(getOrderTotalCop())
      if (amountError) {
        return { ...prev, payment_method: amountError }
      }
      if (!isBoldAmountRangeErrorMessage(prev.payment_method)) return prev
      const next = { ...prev }
      delete next.payment_method
      return next
    })
  }, [
    mode,
    getPaymentMethod,
    getOrderTotalCop,
    formData.lineItems,
    formData.extraDetails?.cliente_neto,
    productDetails,
    costoEnvio
  ])

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: string } = {}
    const paymentMethod = (formData.extraDetails?.payment_method || formData.payment_method || '').toString().trim().toLowerCase()
    const isPlanSepare = paymentMethod === 'plan_separe'
    const isPartOrPlanSepare = paymentMethod === 'part' || isPlanSepare
    const pagosExtraDetails = formData.extraDetails?.pagos
    const plazoValue =
      formData.extraDetails?.plazo ??
      (Array.isArray(pagosExtraDetails) && pagosExtraDetails.length > 0
        ? pagosExtraDetails[0]?.plazo
        : undefined)
    
    // Validar información general
    if (!formData.status) newErrors.status = 'El estado es obligatorio'
    if (!formData.extraDetails?.tipo_lead) newErrors.tipo_lead = 'El tipo de lead es obligatorio'
    if (!formData.extraDetails?.tipo_cliente) newErrors.tipo_cliente = 'El tipo de cliente es obligatorio'
    if (!formData.extraDetails?.marca) newErrors.marca = 'La marca es obligatoria'
    if (!paymentMethod) newErrors.payment_method = 'El método de pago es obligatorio'

    if (mode === 'create' && paymentMethod === 'bold') {
      const boldAmountError = getBoldOrderAmountValidationError(getOrderTotalCop())
      if (boldAmountError) newErrors.payment_method = boldAmountError
    }

    // Calcular forma_pago automáticamente si falta pero hay payment_method
    let formaPagoCalculada = formData.extraDetails?.forma_pago
    if (!formaPagoCalculada && paymentMethod) {
      if (paymentMethod === 'cod' || paymentMethod === 'cmdm' || paymentMethod === 'addi' || paymentMethod === 'plan_separe') {
        formaPagoCalculada = '4' // Crédito/Contra entrega
      } else if (paymentMethod === 'bold') {
        formaPagoCalculada = '6' // Contado
      } else if (paymentMethod === 'part') {
        // Para pagos parciales, depende de si hay crédito en los pagos
        const tieneCredito = pagosParciales.some(pago => 
          pago.metodo_pago === 'cod' || pago.metodo_pago === 'cmdm' || pago.metodo_pago === 'addi'
        )
        formaPagoCalculada = tieneCredito ? '4' : '6'
      } else {
        formaPagoCalculada = '6' // Por defecto contado
      }
    }
    if (paymentMethod === 'dataf') formaPagoCalculada = '6'

    // Validar forma_pago (usando el valor calculado si no existe)
    if (!formaPagoCalculada) {
      newErrors.forma_pago = 'La forma de pago es obligatoria'
    }
    if (!formData.store_name) newErrors.store_name = 'La tienda es obligatoria'
    else {
      const user = JSON.parse(localStorage.getItem('user') || '{}') as { role_id?: number }
      if (!isStoreAllowedForRole(user.role_id, formData.store_name)) {
        newErrors.store_name = 'Tu rol solo permite tiendas marketplace (Mercado Libre, Falabella, Éxito)'
      }
    }
    // El agente solo es obligatorio en modo create
    if (mode === 'create' && !formData.agente) newErrors.agente = 'El agente es obligatorio'
    // Validar ID Transacción en modo create y edit cuando el método de pago lo exige
    if (
      (mode === 'create' || mode === 'edit') && 
      (paymentMethod === 'wompi' || 
       paymentMethod === 'mpago' || 
       paymentMethod === 'bacs' ||
       paymentMethod === 'addi') && 
      !formData.extraDetails?.transaction_id
    ) {
      newErrors.transaction_id = 'El ID de transacción es obligatorio'
    }
    if ((paymentMethod === 'cmdm' || paymentMethod === 'cod' || paymentMethod === 'plan_separe') && !plazoValue) {
      newErrors.plazo = 'El plazo es obligatorio'
    }

    if (
      isPlanSepare &&
      paymentMethod &&
      paymentMethod === 'plan_separe' &&
      formData.status &&
      !['verify_transaction', 'abonado'].includes((formData.status || '').toString().trim().toLowerCase())
    ) {
      newErrors.status = 'Plan Separe: use estado Verificar transacción o Abonado'
    }
    
    // Validar información del cliente
    if (!formData.customer?.first_name) newErrors.first_name = 'El nombre es obligatorio'
    if (!formData.customer?.last_name) newErrors.last_name = 'El apellido es obligatorio'
    if (!formData.customer?.email) {
      newErrors.email = 'El email es obligatorio'
    } else if (formData.customer.email.toLowerCase().endsWith('.con')) {
      newErrors.email = 'El dominio .con no es válido. Por favor, use un dominio válido como .com, .co, etc.'
    }
    if (!formData.customer?.tipo_documento) newErrors.tipo_documento = 'El tipo de documento es obligatorio'
    if (!formData.customer?.document) newErrors.document = 'El documento es obligatorio'

    if (recogeEnBodega && bodegaRecogidaSeleccion === '') {
      newErrors.bodega_recogida = 'Seleccione la bodega de recogida'
    }

    // Validar dirección de facturación (solo si no recoge en bodega)
    if (!recogeEnBodega) {
      if (!formData.billingAddress?.address_1) newErrors.billing_address = 'La dirección de facturación es obligatoria'
      if (!formData.billingAddress?.state) newErrors.billing_state = 'El departamento de facturación es obligatorio'
      if (!formData.billingAddress?.city) newErrors.billing_city = 'La ciudad de facturación es obligatoria'

      // Validar dirección de envío
      if (!formData.shippingAddress?.address_1) newErrors.shipping_address = 'La dirección de envío es obligatoria'
      if (!formData.shippingAddress?.state) newErrors.shipping_state = 'El departamento de envío es obligatorio'
      if (!formData.shippingAddress?.city) newErrors.shipping_city = 'La ciudad de envío es obligatoria'
    }
    
    // El teléfono siempre es obligatorio
    if (!formData.shippingAddress?.phone && !formData.billingAddress?.phone) {
      newErrors.phone = 'El teléfono es obligatorio'
    }

    // Validar productos
    if (!formData.lineItems?.length) newErrors.lineItems = 'Debe agregar al menos un producto'

    // Validar pagos parciales si el método de pago es "part"
    if (isPartOrPlanSepare) {
      if (pagosParciales.length === 0) {
        newErrors.pagos = isPlanSepare ? 'Debe agregar al menos un abono' : 'Debe agregar al menos un pago parcial'
      } else {
        pagosParciales.forEach((pago, index) => {
          if (!pago.metodo_pago) {
            newErrors[`pago_parcial_metodo_${index}`] = 'El método de pago es obligatorio'
          }
          if (
            (pago.metodo_pago === 'wompi' ||
              pago.metodo_pago === 'mpago' ||
              pago.metodo_pago === 'bacs' ||
              (isPlanSepare && pago.metodo_pago === 'addi')) &&
            !pago.id_transaccion
          ) {
            newErrors[`pago_parcial_transaction_${index}`] = 'El ID de transacción es obligatorio'
          }
          const valorRedondeado = Math.round(Number(pago.valor) || 0)
          if (!valorRedondeado || valorRedondeado <= 0) {
            newErrors[`pago_parcial_valor_${index}`] = 'El valor debe ser mayor a 0'
          } else if (
            String(pago.metodo_pago || '').trim().toLowerCase() === 'bold' &&
            valorRedondeado < BOLD_PARTIAL_MIN_AMOUNT_COP
          ) {
            newErrors[`pago_parcial_valor_${index}`] =
              `Bold exige mínimo $${BOLD_PARTIAL_MIN_AMOUNT_COP.toLocaleString('es-CO')} COP por tramo`
          }
        })

        const costoEnvioStr = String(
          costoEnvio !== undefined && costoEnvio !== null
            ? costoEnvio
            : formData.extraDetails?.costo_envio ?? formData.envio ?? '0'
        )
        const totals = calculateTotals(
          formData.lineItems || [],
          productDetails,
          costoEnvioStr,
          isClienteNetoActive(formData.extraDetails)
        )
        const totalPedido = Math.round(
          parseFloat(totals.total || '0') + parseFloat(totals.shipping || '0')
        )
        const sumaPagos = Math.round(pagosParciales.reduce((sum, pago) => sum + (Number(pago?.valor || 0) || 0), 0))

        if (sumaPagos > totalPedido) {
          newErrors.pagos = 'La suma de abonos no puede superar el total del pedido'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, mode, recogeEnBodega, bodegaRecogidaSeleccion, pagosParciales, productDetails, costoEnvio, getOrderTotalCop])

  // Verificar stock disponible
  const isStockAvailable = useCallback(() => {
    const stockOptions = getOrderStockDisplayOptions(recogeEnBodega, bodegaRecogidaSeleccion)
    return isStockAvailableUtil(mode, formData, productDetails, stockOptions)
  }, [mode, formData, productDetails, recogeEnBodega, bodegaRecogidaSeleccion])

  // Obtener resumen de campos faltantes (para toast)
  const getValidationSummary = useCallback(() => {
    const missingFields: string[] = []
    const paymentMethod = (formData.extraDetails?.payment_method || formData.payment_method || '').toString().trim().toLowerCase()
    const isPlanSepare = paymentMethod === 'plan_separe'
    const isPartOrPlanSepare = paymentMethod === 'part' || isPlanSepare
    const pagosExtraDetails = formData.extraDetails?.pagos
    const plazoValue =
      formData.extraDetails?.plazo ??
      (Array.isArray(pagosExtraDetails) && pagosExtraDetails.length > 0
        ? pagosExtraDetails[0]?.plazo
        : undefined)
    
    // Información general
    if (!formData.status) missingFields.push('Estado')
    if (!formData.extraDetails?.tipo_lead) missingFields.push('Tipo de Lead')
    if (!formData.extraDetails?.tipo_cliente) missingFields.push('Tipo de Cliente')
    if (!formData.extraDetails?.marca) missingFields.push('Marca')
    if (!paymentMethod) missingFields.push('Método de Pago')

    if (mode === 'create' && paymentMethod === 'bold') {
      const boldAmountError = getBoldOrderAmountValidationError(getOrderTotalCop())
      if (boldAmountError) missingFields.push('Total del pedido (rango Bold)')
    }

    // Calcular forma_pago automáticamente si falta pero hay payment_method
    let formaPagoCalculada = formData.extraDetails?.forma_pago
    if (!formaPagoCalculada && paymentMethod) {
      if (paymentMethod === 'cod' || paymentMethod === 'cmdm' || paymentMethod === 'addi' || paymentMethod === 'plan_separe') {
        formaPagoCalculada = '4' // Crédito/Contra entrega
      } else if (paymentMethod === 'bold') {
        formaPagoCalculada = '6' // Contado
      } else if (paymentMethod === 'part') {
        // Para pagos parciales, depende de si hay crédito en los pagos
        const tieneCredito = pagosParciales.some(pago => 
          pago.metodo_pago === 'cod' || pago.metodo_pago === 'cmdm' || pago.metodo_pago === 'addi'
        )
        formaPagoCalculada = tieneCredito ? '4' : '6'
      } else {
        formaPagoCalculada = '6' // Por defecto contado
      }
    }
    if (paymentMethod === 'dataf') formaPagoCalculada = '6'

    // Solo agregar a missingFields si realmente falta (no se puede calcular)
    if (!formaPagoCalculada) missingFields.push('Forma de Pago')
    if (!formData.store_name) missingFields.push('Tienda')
    
    // Cliente
    if (!formData.customer?.first_name) missingFields.push('Nombre del Cliente')
    if (!formData.customer?.last_name) missingFields.push('Apellido del Cliente')
    if (!formData.customer?.email) missingFields.push('Email del Cliente')
    if (!formData.customer?.tipo_documento) missingFields.push('Tipo de Documento')
    if (!formData.customer?.document) missingFields.push('Documento')
    
    if (recogeEnBodega && bodegaRecogidaSeleccion === '') {
      missingFields.push('Bodega de recogida')
    }

    // Direcciones (solo si no recoge en bodega)
    if (!recogeEnBodega) {
      if (!formData.billingAddress?.address_1) missingFields.push('Dirección de Facturación')
      if (!formData.billingAddress?.state) missingFields.push('Departamento de Facturación')
      if (!formData.billingAddress?.city) missingFields.push('Ciudad de Facturación')
      if (!formData.shippingAddress?.address_1) missingFields.push('Dirección de Envío')
      if (!formData.shippingAddress?.state) missingFields.push('Departamento de Envío')
      if (!formData.shippingAddress?.city) missingFields.push('Ciudad de Envío')
    }
    
    // Teléfono
    if (!formData.shippingAddress?.phone && !formData.billingAddress?.phone) {
      missingFields.push('Teléfono')
    }
    
    // Productos
    if (!formData.lineItems?.length) missingFields.push('Productos del Pedido')
    
    // ID Transacción
    if (
      (mode === 'create' || mode === 'edit') && 
      (paymentMethod === 'wompi' || 
       paymentMethod === 'mpago' || 
       paymentMethod === 'bacs' ||
       paymentMethod === 'addi') && 
      !formData.extraDetails?.transaction_id
    ) {
      missingFields.push('ID de Transacción')
    }
    if (
      paymentMethod === 'cmdm' ||
      paymentMethod === 'cod' ||
      paymentMethod === 'plan_separe'
    ) {
      if (!plazoValue) {
        missingFields.push('Plazo')
      }
    }

    if (
      isPlanSepare &&
      formData.status &&
      !['verify_transaction', 'abonado'].includes((formData.status || '').toString().trim().toLowerCase())
    ) {
      missingFields.push('Estado (Verificar transacción o Abonado)')
    }

    if (isPartOrPlanSepare && (!pagosParciales || pagosParciales.length === 0)) {
      missingFields.push(isPlanSepare ? 'Al menos un Abono' : 'Al menos un Pago Parcial')
    }

    if (isPartOrPlanSepare && pagosParciales?.length) {
      pagosParciales.forEach((pago, index) => {
        const vm = Math.round(Number(pago?.valor) || 0)
        if (
          String(pago?.metodo_pago || '').trim().toLowerCase() === 'bold' &&
          vm > 0 &&
          vm < BOLD_PARTIAL_MIN_AMOUNT_COP
        ) {
          missingFields.push(
            `${isPlanSepare ? 'Abono' : 'Pago'} ${index + 1} (Bold): mínimo $${BOLD_PARTIAL_MIN_AMOUNT_COP.toLocaleString('es-CO')} COP`
          )
        }
      })
    }
    
    return missingFields
  }, [formData, mode, recogeEnBodega, bodegaRecogidaSeleccion, pagosParciales, getOrderTotalCop])

  return {
    errors,
    setErrors,
    validateForm,
    isStockAvailable,
    isBoldPaymentAmountValid,
    getValidationSummary
  }
}

